import { postToStripeServer } from './subscriptionActions';

/** Plan de pause calculé par le serveur (secondes Unix, comme Stripe). */
export type PausePlan = {
    months: 1 | 2 | 3;
    /** Fin de la période payée : on joue jusque-là. */
    startsAt: number;
    /** Levée de la pause (3 jours avant la reprise du prélèvement) : les jeux reviennent. */
    resumesAt: number;
    /** Échéance de reprise, prélevée. */
    endsAt: number;
    /** Échéances non prélevées. */
    skippedChargeDates: number[];
};

export type SubscriptionPause = {
    status: 'paused' | 'resumed' | 'canceled' | 'ended';
    months: number | null;
    startsAt: number | null;
    resumesAt: number | null;
    endsAt: number | null;
    source: string | null;
    /** Annuler cette pause avant son début rétablit une résiliation programmée. */
    restoresCancellation?: boolean;
};

export type PauseReason =
    | 'disabled'
    | 'no_subscription'
    | 'trialing'
    | 'payment_issue'
    | 'not_active'
    | 'already_paused'
    | 'has_schedule'
    | 'not_monthly'
    | 'cancel_scheduled'
    | 'no_period'
    | 'period_ending'
    | 'recently_paused';

export type SubscriptionStatus = {
    /** Statut Stripe (`trialing`, `active`, `past_due`, `canceled`…) ou `none`. */
    status: string;
    /** Timestamps Unix (secondes), comme Stripe. */
    trialStart: number | null;
    trialEnd: number | null;
    currentPeriodEnd: number | null;
    cancelAtPeriodEnd: boolean;
    /** Fin programmée par date seule (facturation flexible), secondes Unix. Absent = ancien serveur. */
    cancelAt?: number | null;
    /** Remise en place (code partenaire) : le prix catalogue ne vaut pas pour ce client. */
    discounted?: boolean;
    /** Devise réellement facturée (`eur`, `usd`, `gbp`). */
    currency: string | null;
    /** Prochain prélèvement en centimes, remises comprises (null si inconnu). */
    nextAmount?: number | null;
    interval?: string | null;
    /** Nombre d'intervalles par période (1 = mensuel simple). */
    intervalCount?: number | null;
    /** Pause en cours ou programmée (null sinon). */
    pause?: SubscriptionPause | null;
    /** Offre de pause proposable. Absent (ancien serveur) = non : la console masque l'offre. */
    canPause?: boolean;
    pauseReason?: PauseReason | null;
    nextPauseEligibleAt?: number | null;
    pauseOptions?: PausePlan[];
    /** Rôle de l'utilisateur dans l'établissement : les actions sont réservées aux admins. */
    viewerRole?: 'admin' | 'member';
    /**
     * Ancien essai jamais payé, parti depuis ~60 j : remise appliquée d'office
     * au 1er mois s'il réactive (stripe-b2b, coupon RETOUR_ESSAI). Seulement
     * quand il n'y a plus d'abonnement vivant ; absent = ancien serveur.
     */
    returnOffer?: { percentOff: number } | null;
    /**
     * Formules proposables en un clic, calculées par stripe-b2b avec les règles exactes de ses
     * boutons (proPlan.js `planOptions`) : `upgrades` ouvrables sur l'écran de confirmation,
     * `ended` = plus d'abonnement en cours (« Passer en Pro » → page de paiement). Absent = ancien
     * serveur ou lecture ratée : la console garde ses propres règles.
     */
    planOptions?: { upgrades: ('annual' | 'pro' | 'pro_annual')[]; ended: boolean } | null;
};

/**
 * État de l'abonnement Stripe d'un établissement (page Mon compte, écran
 * « Plutôt faire une pause ? »). Lecture via stripe-b2b, authentifiée (jeton
 * Firebase, admin de l'établissement) : Firestore ne connaît ni la fin d'essai
 * ni le passage trialing → active.
 *
 * `context: 'cancel_flow'` → le serveur journalise l'ouverture de l'écran de
 * résiliation (billingEvents).
 *
 * Renvoie `null` si le serveur est injoignable ou refuse (non-admin…) : la page
 * doit s'afficher sans (l'accès au portail reste disponible).
 */
export async function getSubscriptionStatus(
    clientId: string,
    options: { context?: 'cancel_flow' } = {}
): Promise<SubscriptionStatus | null> {
    try {
        return await postToStripeServer<SubscriptionStatus>('/subscription-status', {
            clientId,
            ...(options.context && { context: options.context }),
        });
    } catch {
        return null;
    }
}
